import { Autocomplete, Button, DatePicker, Field, Text, TextField } from "components/commons";
import styles from "./bank-deposit.module.scss";
import { useFormContext } from "contexts/form-context";
import { isString } from "lodash";
import { FormMode, Path } from "enums";
import { InputAmount, SelectShiftsPaginated } from "components/field";
import SelectFile, { extractDirectoryWithFile } from "components/commons/select-file/select-file";
import { Chip } from "@material-ui/core";
import locale from "localization";
import TimePickerV2 from "components/commons/time-picker/time-picker-v2";
import moment from "moment";
import { useRouter } from "hooks";
import { depositTypeOptions } from "data/constant/bank-deposit";
import { useMemo, useState } from "react";
import EmployeePositionEnum from "enums/employee-position";
import { ShiftStatus } from "enums";

const BankDepositForm = ({ loading }) => {
  const { formMode, fields, modifyField, isFormSubmittable } = useFormContext();
  const router = useRouter();
  const isAdd = formMode === FormMode.Add;
  const isEdit = formMode === FormMode.Edit;
  const [errorFile, setErrorFile] = useState(false);

  const isDateToday = useMemo(
    () => moment(fields.date.value).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD"),
    [fields.date.value]
  );

  return (
    <div className={styles.form}>
      <div className={styles.dateRow}>
        <Field {...fields.date} label={locale.date} childrenClassName={styles.fieldContent}>
          <DatePicker
            {...fields.date}
            prettify={(date) => moment(date).format("YYYY-MM-DD HH:mm")}
            onChange={modifyField}
            Component={DatePicker}
            disableEndDate={new Date()}
            disabled={isEdit}
          />
        </Field>
        <Field {...fields.time} label={locale.time} childrenClassName={styles.fieldContent}>
          <TimePickerV2
            {...fields.time}
            variant={"purple"}
            onChange={modifyField}
            Component={TimePickerV2}
            maxTime={isDateToday ? new Date() : undefined}
            date={fields.date.value}
            disabled={isEdit}
          />
        </Field>
      </div>
      <Field
        {...fields.shiftId}
        label={locale.shift}
        labelClassName={styles.fieldLabel}
        childrenClassName={styles.fieldContentShift}
      >
        <SelectShiftsPaginated
          {...fields.shiftId}
          disableAllOption
          shiftStatus={[ShiftStatus.Ended]}
          onChange={modifyField}
          placeholder={locale.selectShift}
          isHideBankDeposit={true}
          isFlattenedRecord={true}
        />
      </Field>
      <Field {...fields.type} label={locale.depositType} childrenClassName={styles.fieldContent}>
        <Autocomplete
          {...fields.type}
          onChange={modifyField}
          options={depositTypeOptions}
          Component={Autocomplete}
        />
      </Field>
      <Field
        {...fields.amount}
        label={locale.amount}
        message={<locale.Populate items={["11"]} text={locale.maximumDigitsAndDecimalPlaces} />}
        style={{
          width: "250px",
        }}
      >
        <InputAmount {...fields.amount} onChange={modifyField} Component={InputAmount} />
      </Field>
      <Text color={"black"} strong style={{ fontSize: "16px" }}>
        {locale.bankDepositSlip}
      </Text>
      <Text subtitle color="black">
        {locale.maxFileSize5MB}
      </Text>
      <Field {...fields.slip} childrenClassName={styles.fieldContent}>
        <SelectFile
          {...fields.slip}
          onChange={(name, props) => {
            setErrorFile(props?.error);
            modifyField(name, props);
          }}
          onClick={() => {
            setErrorFile("");
          }}
          renderResult={() => {
            const label = isString(fields?.slip?.value)
              ? extractDirectoryWithFile(fields?.slip?.value)
              : fields?.slip?.value?.name;
            return (
              <Chip
                label={label}
                title={label}
                style={{
                  backgroundColor: "#eae8ff",
                  color: "#5349b7",
                  fontWeight: "500",
                  maxWidth: "300px",
                }}
                onDelete={() => {
                  modifyField(fields.slip.name, { value: null });
                }}
                size="small"
              />
            );
          }}
          errorFileFormatMessage={locale.failedToUploadImage}
          errorStyle={{
            width: "320px",
          }}
          maxSizeKb={5000}
          isView={formMode === FormMode.View}
        />
      </Field>
      <Field {...fields.remarks} label={locale.remarks}>
        <TextField multiline {...fields?.remarks} onChange={modifyField} />
      </Field>
      <div style={{ display: "flex", gap: "1em" }}>
        {isEdit && (
          <Button
            variant="outlined"
            onClick={() => router.push(Path.BankDeposit)}
            loading={loading}
          >
            {locale.cancel}
          </Button>
        )}
        {(isAdd || isEdit) && (
          <Button
            type="submit"
            primary
            disabled={!isFormSubmittable || loading || errorFile}
            loading={loading}
          >
            {isEdit ? locale.saveChanges : locale.submit}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BankDepositForm;
